import { Button } from '@/components/ui/button';
import { useContentDetail } from '@/hooks/useContentQueries';
import { isUnsetOrNzPlaceholder } from '@/utils/content-utils';
import { ChevronUp, ExternalLink, MoreHorizontal } from 'lucide-react';
import { useMemo, useState } from 'react';

interface ProvenanceContentCardProps {
  contentId: number;
  fallbackName: string;
  fallbackDescription?: string;
  onOpenContent?: (contentId: number) => void;
}

export function ProvenanceContentCard({
  contentId,
  fallbackName,
  fallbackDescription,
  onOpenContent,
}: ProvenanceContentCardProps) {
  const [expanded, setExpanded] = useState(false);
  const { data: content } = useContentDetail(contentId, { enabled: contentId > 0 });

  const displayTitle = useMemo(() => {
    if (content?.title && !isUnsetOrNzPlaceholder(content.title) && content.title.trim()) {
      return content.title.trim();
    }
    if (!isUnsetOrNzPlaceholder(fallbackName) && fallbackName.trim()) {
      return fallbackName.trim();
    }
    return 'Processing document…';
  }, [content?.title, fallbackName]);

  const description = useMemo(() => {
    if (content?.description && !isUnsetOrNzPlaceholder(content.description)) {
      return content.description.trim();
    }
    if (fallbackDescription && !isUnsetOrNzPlaceholder(fallbackDescription)) {
      return fallbackDescription.trim();
    }
    return null;
  }, [content?.description, fallbackDescription]);

  const placeholderText = useMemo(() => {
    if (!isUnsetOrNzPlaceholder(fallbackName) && fallbackName.trim()) {
      return 'Waiting for summary…';
    }
    return 'Waiting for generated title and summary…';
  }, [fallbackName]);

  return (
    <div
      data-testid="history-content-card"
      data-content-id={String(contentId)}
      data-content-type={content?.contentType ?? ''}
      className="w-full rounded border p-2 space-y-2 text-sm text-card-foreground"
    >
      <div className="flex items-center justify-between gap-2">
        <p data-testid="history-content-card-title" className="font-medium text-xs">{displayTitle}</p>
        {onOpenContent ? (
          <Button
            data-testid="history-content-card-open"
            type="button"
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            aria-label="Open content"
            title="Open content"
            onClick={() => onOpenContent(contentId)}
          >
            <ExternalLink className="h-3 w-3.5" />
          </Button>
        ) : null}
      </div>
      {description ? (
        <div className="flex items-start gap-1">
          <p className={`text-xs text-muted-foreground flex-1 ${expanded ? 'whitespace-pre-wrap' : 'truncate'}`}>
            {description}
          </p>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-6 w-6 shrink-0"
            onClick={() => setExpanded((previous) => !previous)}
            title={expanded ? 'Show less' : 'Show more'}
          >
            {expanded ? <ChevronUp className="h-3 w-3.5" /> : <MoreHorizontal className="h-3 w-3.5" />}
          </Button>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">{placeholderText}</p>
      )}
    </div>
  );
}
